import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from "@angular/core";
import {Subscription} from "rxjs";
import {Notification} from "src/app/interfaces/notification.interface";
import {User} from "src/app/modules/auth/models/User";
import {AuthService} from "src/app/modules/auth/services/auth.service";
import {ModalService} from "src/app/modules/core/services/modal.service";
import {ToastService} from "src/app/modules/core/services/toast.service";
import {CustomError} from "src/app/modules/shared/interfaces/CustomError.interface";
import {ScreenSize} from "src/app/modules/shared/interfaces/ScreenSize.interface";
import {ClickService} from "src/app/modules/shared/services/click.service";
import {ScreenSizeService} from "src/app/modules/shared/services/screenSize.service";
import {UserService} from "src/app/modules/shared/services/user.service";
import {NotificationsService} from "src/app/services/notifications.service";
import {RealtimeInvalidationService} from "src/app/modules/shared/services/realtime-invalidation.service";
import {NgClass} from "@angular/common";
import {NotificationsListComponent} from "../notifications-list/notifications-list.component";
import {ModalComponent} from "../../modules/core/components/modal/modal.component";
import {TranslateModule} from "@ngx-translate/core";

@Component({
  selector: "app-notifications",
  templateUrl: "./notifications.component.html",
  styleUrls: ["./notifications.component.css"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgClass,
    NotificationsListComponent,
    ModalComponent,
    TranslateModule,
  ],
})
export class NotificationsComponent implements OnInit, OnDestroy {
  @Input() user!: User;
  public notifications?: Notification[];
  public unreadNotifications?: Notification[];
  public isMobile = false;
  public isSubmenuOpen = false;
  private readonly notificationsUpdatedCallback = () => {
    this.fetchNotifications();
    this.cdr.markForCheck();
  };
  private readonly subscriptions: Subscription[] = [];
  private socketCleanup?: () => void;
  private fetchSubscription?: Subscription;

  constructor(
    private readonly screenSizeService: ScreenSizeService,
    private readonly notificationsService: NotificationsService,
    private readonly modalService: ModalService,
    private readonly clickService: ClickService,
    private readonly cdr: ChangeDetectorRef,
    private readonly toastService: ToastService,
    private readonly userService: UserService,
    private readonly authService: AuthService,
    private readonly realtimeInvalidationService: RealtimeInvalidationService
  ) {
    const screenSizeSubscription = this.screenSizeService.screenSize$.subscribe(
      (size: ScreenSize) => {
        this.isMobile = size.isMobile;
      }
    );

    const clickSubscription = this.clickService
      .getClickObservable()
      .subscribe((event: Event) => {
        const targetElement = event.target as HTMLElement;
        if (
          (!targetElement.closest(".notifications") &&
            !targetElement.closest(".pushBtns")) ||
          targetElement.closest(".notificationLink")
        ) {
          this.isSubmenuOpen = false;
          this.cdr.markForCheck();
        }
      });
    this.subscriptions.push(screenSizeSubscription, clickSubscription);
  }

  ngOnInit() {
    this.subscriptions.push(
      this.notificationsService.notificationsPanelClose$.subscribe(() => {
        this.isSubmenuOpen = false;
        this.cdr.markForCheck();
      })
    );
    const notificationsPanelOpenSubscription =
      this.notificationsService.notificationsPanelOpen$.subscribe(
        (notifications) => {
          this.notifications = notifications;

          if (this.isMobile) {
            this.modalService.openNotificationsModal(
              "notifications",
              this.notifications
            );
          } else {
            this.isSubmenuOpen = true;
          }

          this.cdr.markForCheck();
        }
      );

    this.subscriptions.push(notificationsPanelOpenSubscription);

    const unreadSubscription = this.notificationsService
      .getNotifications()
      .subscribe((updatedNotifications) => {
        this.notifications = updatedNotifications;
        this.unreadNotifications = updatedNotifications.filter((n) =>
          this.notificationsService.isEffectivelyUnread(n)
        );
        this.cdr.markForCheck();
      });
    this.subscriptions.push(unreadSubscription);

    this.fetchNotifications();

    this.socketCleanup = this.realtimeInvalidationService.subscribe(
      "newNotification",
      this.notificationsUpdatedCallback,
      {key: "notifications:user"}
    );
  }

  fetchNotifications() {
    this.fetchSubscription?.unsubscribe();
    this.fetchSubscription = new Subscription();
    const userSubscription = this.userService.getUser(this.user._id).subscribe({
      next: (user: User | null) => {
        if (user) {
          this.authService.user = user;
          this.user = user;
          this.cdr.markForCheck();
        }
      },
    });

    const userNotificationsSubscription = this.notificationsService
      .getUserNotifications(this.user._id)
      .subscribe((res) => {
        if (res?.data) {
          this.notifications = res.data as Notification[];
          this.notificationsService.notificationsSubject.next(
            this.notifications
          );
          this.cdr.markForCheck();
        }
      });

    this.fetchSubscription.add(userSubscription);
    this.fetchSubscription.add(userNotificationsSubscription);
  }

  toggleSubmenu() {
    if (this.isMobile) {
      this.modalService.openNotificationsModal(
        "notifications",
        this.notifications
      );
    }
    this.isSubmenuOpen = !this.isSubmenuOpen;
  }

  closeModal(modalId: string): void {
    this.modalService.closeModal(modalId);
  }

  deleteNotifications() {
    this.notificationsService.deleteUserNotifications(this.user._id).subscribe({
      next: () => {
        this.closeModal("dlt_notifications");
        this.toastService.sendToast({
          text: "NOTIFICATIONS_DELETED",
          classes: "bg-form-success",
        });
        this.notificationsService.setNotifications([]);
        this.cdr.markForCheck();
      },
      error: (error: CustomError) => {
        this.toastService.sendToast({
          text: error.error.error,
          classes: "bg-form-error",
        });
      },
    });
  }

  onKeyUp(event: KeyboardEvent, type: string, modalId?: string): void {
    if (event.key === "Enter" || event.key === " ") {
      if (type === "submenu") {
        this.toggleSubmenu();
      } else if (type === "closeModal" && modalId) {
        this.closeModal(modalId);
      } else if (type === "deleteNotifications") {
        this.deleteNotifications();
      }
    }
  }

  ngOnDestroy() {
    for (const sub of this.subscriptions) {
      sub.unsubscribe();
    }
    this.socketCleanup?.();
    this.fetchSubscription?.unsubscribe();
  }
}
